<script lang="ts" setup>
import DropDown, { type DropDownItem } from '@/components/common/DropDown.vue';
import FormattedNumberInput from '@/components/common/formatted-number-input.vue';
import SimpleTable from '@/components/tables/SimpleTable.vue';
import { fetchAccounts } from '@/utils/accounts/fetch-accounts';
import type { BaseColumnData } from '@/utils/tables/column-data';
import { PhPlus, PhTrash } from '@phosphor-icons/vue';
import { calculateAmount, models, units, utils } from 'cesg-shared';
import { onMounted, type PropType, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  data: {
    type: Array as () => Array<models.AtaCost>,
    required: true,
    default: () => [],
  },
  readOnly: {
    type: Boolean as PropType<boolean>,
    required: true,
    default: () => false,
  },
  debitNormRates: {
    type: Object as PropType<Record<number, number>>,
    default: () => ({}),
  },
});
const costs = ref<models.AtaCost[]>([]);
const accounts = ref<DropDownItem[]>([]);

const columns: BaseColumnData[] = [
  {
    key: 'accountNumber',
    label: t('ata.details.costs.accountNumber'),
    editable: () => true,
    required: true,
  },
  {
    key: 'description',
    label: t('ata.details.costs.description'),
    editable: () => true,
  },
  {
    key: 'numberOfItems',
    label: t('ata.details.costs.numberOfItems'),
    editable: () => true,
    format: utils.formatNumber,
    editableInputType: 'number',
  },
  { key: 'unit', label: t('ata.details.costs.unit'), editable: () => true },
  {
    key: 'pricePerUnit',
    label: t('ata.details.costs.pricePerUnit'),
    editable: () => true,
    format: utils.formatNumber,
    editableInputType: 'number',
    align: 'has-text-right',
    required: true,
  },
  {
    key: 'markupFactor',
    label: t('ata.details.costs.markupFactor'),
    editable: () => true,
    format: utils.formatNumber,
    editableInputType: 'number',
    align: 'has-text-right',
    prefix: '%',
  },
  {
    key: 'amount',
    label: t('ata.details.costs.amount'),
    format: utils.formatNumber,
    align: 'has-text-right',
  },
  {
    key: 'delete',
    align: 'has-text-right',
  },
];

const onAccountSelected = (row: models.AtaCost, accountNumber: string) => {
  const parsedAccountNumber = Number(accountNumber);

  if (!accountNumber || isNaN(parsedAccountNumber) || parsedAccountNumber < 1) {
    row.accountNumber = -1;
    row.pricePerUnit = 0;
    return;
  }

  row.accountNumber = parsedAccountNumber;
  const rate = props.debitNormRates[parsedAccountNumber];
  if (rate !== undefined) {
    row.pricePerUnit = rate;
    row.unit = units.Hour;
  } else {
    row.pricePerUnit = 0;
  }
};

const addCostRow = () => {
  const newRow: models.AtaCost = {
    accountNumber: -1,
    description: '',
    amount: 0,
    numberOfItems: 1,
    unit: units.Piece,
    markupFactor: 0,
    pricePerUnit: 0,
  };

  costs.value.push(newRow);
};

const removeChild = (index: number) => {
  if (!costs.value || index < 0 || index >= costs.value.length) return;
  costs.value.splice(index, 1);
};

watch(
  () => props.data,
  (newData) => {
    costs.value = newData;
  },
);

watch(
  () => costs.value,
  () => {
    costs.value.forEach((cost) => {
      cost.amount = calculateAmount(
        cost.pricePerUnit,
        cost.numberOfItems,
        cost.markupFactor,
      );
    });
  },
  { deep: true },
);

// Re-point the local ref at the prop array whenever the parent hands down a
// fresh one (e.g. after a save reloads the ÄTA). Without this the ref keeps
// referencing the array captured at mount time, so post-reload edits mutate a
// detached array, the form never registers as dirty, and Save stays disabled.
watch(
  () => props.data,
  (data) => {
    costs.value = data;
  },
  { immediate: true },
);

onMounted(async () => {
  accounts.value = await fetchAccounts();
});
</script>
<template>
  <simple-table
    :data="costs"
    :columns="columns"
    :sum-column-keys="['amount']"
    :row-clickable="false"
  >
    <template #tbody="{ data }">
      <tr v-for="(row, rowIndex) in data" :key="rowIndex">
        <td
          v-for="(column, columnIndex) in columns"
          :key="columnIndex"
          :class="[
            column.align ? column.align : '',
            column.class ? column.class(row[column.key]) : '',
          ]"
          class="is-vcentered"
        >
          <span v-if="column.editable">
            <template v-if="column.key === 'accountNumber'">
              <drop-down
                :model-value="row[column.key]"
                @update:model-value="onAccountSelected(row, $event)"
                :items="accounts ?? []"
                :label="t('dropdownLabel')"
                :disabled="readOnly"
                teleport
              ></drop-down>
            </template>
            <template v-else-if="column.key === 'unit'">
              <div class="select unit">
                <select v-model="row[column.key]" :disabled="readOnly">
                  <option
                    v-for="(value, key) in units"
                    :key="key"
                    :value="value"
                  >
                    {{ value }}
                  </option>
                </select>
              </div>
            </template>
            <template v-else>
              <div class="field">
                <p
                  class="control"
                  :class="{ 'has-icons-right': column.prefix }"
                >
                  <formatted-number-input
                    v-if="column.editableInputType === 'number'"
                    v-model="row[column.key]"
                    :class="[column.key]"
                    :disabled="readOnly"
                    :required="column.required"
                  />
                  <input
                    v-else
                    class="input"
                    :type="column.editableInputType"
                    v-model="row[column.key]"
                    :class="[column.key]"
                    :disabled="readOnly"
                    :required="column.required"
                  />
                  <span class="icon is-small is-right" v-if="column.prefix">
                    {{ column.prefix }}
                  </span>
                </p>
              </div>
            </template>
          </span>
          <span v-else-if="column.key === 'delete'">
            <button
              type="button"
              class="button is-small is-danger"
              @click="removeChild(rowIndex)"
              :disabled="readOnly"
              data-test="delete-btn"
            >
              <PhTrash :size="18" /></button
          ></span>
          <span v-else>
            {{
              column.format ? column.format(row[column.key]) : row[column.key]
            }}
          </span>
        </td>
      </tr>
      <tr>
        <td>
          <button
            class="button is-small is-light"
            @click="addCostRow()"
            type="button"
            :disabled="readOnly"
            data-test="add-btn"
          >
            <ph-plus size="14" weight="bold" class="mr-1"></ph-plus>
            {{ t('ata.details.addCostButton') }}
          </button>
        </td>
        <td v-for="item in columns.length - 1" :key="item"></td>
      </tr>
    </template>
  </simple-table>
</template>

<style lang="scss" scoped>
input {
  width: 6rem;

  &.description {
    width: auto;
  }
}

.select.account {
  width: 15rem;
}
</style>
